import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, Search, Mail } from 'lucide-react';
import logger from '@/utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactReports = () => {
  const { getAuthHeaders } = useAdmin();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/reports/contacts`, {
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (error) {
      logger.error('Error fetching report:', error);
      toast.error('Failed to fetch contact report');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/contacts/${contactId}/status?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Status updated');
      fetchReport();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportToCSV = () => {
    if (!report?.contacts) return;
    
    const headers = ['Name', 'Email', 'Phone', 'Subject', 'Message', 'Status', 'Created At'];
    const rows = report.contacts.map(c => [
      c.name,
      c.email,
      c.phone || '',
      c.subject,
      c.message.replace(/,/g, ';'),
      c.status || 'new',
      c.created_at
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredContacts = report?.contacts?.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (c.status || 'new') === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contact-reports">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Contact Reports</h1>
          <p className="text-slate-500">View and manage contact form submissions</p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-800">{report?.total || 0}</p>
            <p className="text-sm text-slate-500">Total Messages</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{report?.by_status?.new || 0}</p>
            <p className="text-sm text-slate-500">New</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{report?.by_status?.responded || 0}</p>
            <p className="text-sm text-slate-500">Responded</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-contacts"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="responded">Responded</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contacts List */}
      <div className="space-y-4">
        {filteredContacts.map((contact) => (
          <Card key={contact.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-slate-800">{contact.name}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      (contact.status || 'new') === 'new' ? 'bg-blue-100 text-blue-700' :
                      contact.status === 'responded' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {contact.status || 'new'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">{contact.email} {contact.phone && `• ${contact.phone}`}</p>
                  <p className="font-medium text-slate-700 mb-2">{contact.subject}</p>
                  <p className="text-slate-600 text-sm">{contact.message}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(contact.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={contact.status || 'new'}
                    onValueChange={(value) => updateStatus(contact.id, value)}
                  >
                    <SelectTrigger className="w-32" data-testid={`status-${contact.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="responded">Responded</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {filteredContacts.length === 0 && (
          <Card>
            <CardContent className="text-center py-12 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No contact messages found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ContactReports;
